import { apiRequest } from '../lib/httpClient'
import type { InitDetailRequest } from '../lib/initDetail'
import type { DashboardReport, DetailInitApiPayload, DetailInitResult, UserGroupOption, UserOption } from '../types/dashboard'
import { asArray, pickString } from './shared/coercion'

function mapReportRow(row: Record<string, unknown>): DashboardReport {
  const rawGroups = asArray<unknown>(row['groups'] ?? row['Groups'])
  const rawStatus = String(row['status'] ?? row['Status'] ?? '').trim().toLowerCase()
  const resolvedStatusId = Number(row['statusId'] ?? row['StatusId'] ?? 0) || (rawStatus === 'inactive' ? 2 : 1)

  return {
    id: pickString(row['id'] ?? row['Id'] ?? row['reportId'] ?? row['ReportId']),
    reportId: pickString(row['reportId'] ?? row['ReportId'] ?? row['id'] ?? row['Id']),
    name: pickString(row['name'] ?? row['Name']),
    powerBIReportName: pickString(row['powerBIReportName'] ?? row['PowerBIReportName']),
    groups: rawGroups
      .map((group) => {
        if (typeof group === 'string') return group
        if (!group || typeof group !== 'object') return ''
        return pickString((group as Record<string, unknown>)['name'] ?? (group as Record<string, unknown>)['Name'])
      })
      .filter(Boolean),
    status: resolvedStatusId === 1 ? 'Active' : 'Inactive',
    statusId: resolvedStatusId,
    typeId: Number(row['typeId'] ?? row['TypeId'] ?? 1) === 2 ? 2 : 1,
  }
}

function mapGroupOption(row: Record<string, unknown>): UserGroupOption {
  return {
    value: pickString(row['groupId'] ?? row['GroupId'] ?? row['value'] ?? row['Value'] ?? row['id'] ?? row['Id']),
    name: pickString(row['name'] ?? row['Name'] ?? row['label'] ?? row['Label']),
  }
}

function mapUserOption(row: Record<string, unknown>): UserOption {
  return {
    value: pickString(row['userId'] ?? row['UserId'] ?? row['value'] ?? row['Value'] ?? row['id'] ?? row['Id']),
    name: pickString(
      row['fullName'] ?? row['FullName'] ?? row['name'] ?? row['Name'] ?? row['label'] ?? row['Label']
    ),
  }
}

export function normalizeDetailInit(request: Pick<InitDetailRequest, 'scope'>, payload: Record<string, unknown>): DetailInitResult {
  const options = ((payload['options'] || payload['Options']) as Record<string, unknown> | undefined) || {}
  const grids = ((payload['grids'] || payload['Grids']) as Record<string, unknown> | undefined) || {}
  const client = ((payload['client'] || payload['Client']) as Record<string, unknown> | undefined) || {}

  return {
    scope: request.scope,
    reports: asArray<Record<string, unknown>>(grids['reports'] ?? payload['reports'] ?? payload['Reports']).map(mapReportRow),
    groupOptions: asArray<Record<string, unknown>>(options['groups'] ?? options['userGroups']).map(mapGroupOption),
    userOptions: asArray<Record<string, unknown>>(options['users'] ?? options['userOptions']).map(mapUserOption),
    clientWorkspace: pickString(client['powerBIWorkspace'] ?? client['PowerBIWorkspace']),
    clientWorkspaceName: pickString(client['powerBIWorkspaceName'] ?? client['PowerBIWorkspaceName']),
  }
}

export async function fetchDetailInit(request: InitDetailRequest): Promise<DetailInitResult> {
  const payload = await apiRequest<DetailInitApiPayload>(request.endpoint, {
    method: 'GET',
    query: request.scope === 'user' ? { userId: request.userId } : { clientId: request.clientId },
  })

  return normalizeDetailInit(request, payload)
}
